import { formatUrl, getHrefForProductLink } from '@/utils/linkUtils';
import { isKrugOrWhispering } from '@/utils/millesimaUtils';
import { normalizeUrl } from '@/utils/menuUtils';

import {
  isPrimeurCode,
  isBoxProduct,
  getAttribute,
  getAttributeValue,
  getAttributeId,
  getAttributeUrl,
  getAttributeGroupIdentifier,
  getAttributeIsSpirit,
} from '@/utils/productUtils';
import { ATTRIBUTES } from '@/constants/product';
import { SVG_SERVER_URL } from '@/constants/urls';
import { WINEBOX_MAX } from '@/constants/cart';

export const computeProps = ({ t, currentItem = {}, product, categ, storeConf, wineboxQty, isPanache, quantity,
  onProductSelection, onClickProduct, handleQuantityChange, addItemToCart, position }) => {
  const rating = storeConf.ratingOrder || [];
  const productCode = getAttributeId(product, 'in_stock', '');
  const panache = isBoxProduct(currentItem.partnumber) || isPanache;
  const { specialStyle, specialStyleNotHover } = getPromotionStyle(product, currentItem, productCode);

  const cp = {
    t,
    categ,
    product,
    quantity,
    storeConf,
    wineboxQty,
    currentItem,
    productCode,
    specialStyle,
    addItemToCart,
    onClickProduct,
    isPanache: panache,
    onProductSelection,
    handleQuantityChange,
    specialStyleNotHover,
    isFirstRating: false,
    isSecondRating: false,
    display: product.display,
    locale: storeConf.locale,
    storeId: storeConf.storeId,
    variant: currentItem.shortdesc,
    itemsList: getItemList(product),
    price: getItemPrice(currentItem),
    boxFull: wineboxQty === WINEBOX_MAX,
    nbFormat: product.items?.length ?? 0,
    itemsInPromo: getItemsInPromo(product.items),
    qteMax: getMaxQty(+wineboxQty || 0, currentItem, product),
    region: getAttribute(product, 'region', ''),
    couleur: getAttributeValue(product, 'couleur', ''),
    brand: getAttributeValue(product, 'producteur', ''),
    brandId: getAttributeId(product, 'producteur', ''),
    ribbonadId: getAttributeId(currentItem, 'ribbonad', ''),
    ribbonad: getAttributeValue(currentItem, 'ribbonad', ''),
    infoSite: getAttributeValue(currentItem, 'infoSite', ''),
    urlBrand: getAttributeUrl(product, 'producteur', ''),
    groupIdentifierBrand: getAttributeGroupIdentifier(product, 'producteur', ''),
    isSpiritBrand: getAttributeIsSpirit(product, 'producteur', ''),
    firstRating: getAttribute(product, rating[0]?.name),
    secondRating: getAttribute(product, rating[1]?.name),
    // eslint-disable-next-line eqeqeq
    isAlert: getAttributeId(product, 'alerte_o_n', 0) == 1,
    classement: getAttributeValue(product, 'classement', ''),
    appellation: getAttribute(product, 'appellation', ''),
    producerCateg: getProducerCateg(categ, product, storeConf.storeId),
    typeProduit: getAttribute(product, ATTRIBUTES.TYPE_PRODUIT),
    sousTypeProduit: getAttribute(product, ATTRIBUTES.SOUS_TYPE_PRODUIT),
    mentionAge: getAttribute(product, ATTRIBUTES.MENTION_AGE),
    matierePremiere: getAttribute(product, ATTRIBUTES.MATIERE_PREMIERE),
    origineSpiritueux: getAttribute(product, ATTRIBUTES.ORIGINE_SPIRITUEUX),
    position,
    isSpirit: product.isSpirit
  };

  cp.isFirstRating = cp.firstRating?.name;
  cp.isSecondRating = cp.secondRating?.name;
  cp.firstRatingMax = cp.firstRating?.name && rating[0].ratingMax;
  cp.secondRatingMax = cp.secondRating?.name && rating[1].ratingMax;
  cp.stockLabel = getStockLabel(cp.itemsInPromo.length, cp.ribbonad, product);

  return cp;
};

export const getMaxQty = (initialQty, currentItem, product) => {
  const isWineboxQty = isBoxProduct(currentItem.partnumber);
  const qteWine = +initialQty || 0;
  let maxQty = isWineboxQty ? WINEBOX_MAX - qteWine : currentItem.stock;
  const limitEquivbtl = getAttributeValue(product, 'limit_equivbtl');
  let equivbtl = 1;
  let nbnunit = 1;

  if (currentItem?.attributes) {
    nbnunit = +getAttributeValue(currentItem, 'nbunit');
    equivbtl = +getAttributeValue(currentItem, 'equivbtl');
  }

  if (limitEquivbtl > 0) {
    const totalBtl = nbnunit * equivbtl;
    const qtyEquiv = Math.floor(limitEquivbtl / totalBtl);
    if (maxQty > qtyEquiv) {
      maxQty = qtyEquiv;
    }
  }

  return maxQty;
};

export const getCriticsRatingNotes = (product, storeConf) => {
  return storeConf?.ratingOrder?.map(({ name: nameId, ratingMax, shortName }) => {
    const { name, value } = getAttribute(product, nameId);
    const logo = getCriticLogo(shortName);
    return ratingMax && name && { name, value, ratingMax, shortName, logo };
  })
    .filter(Boolean) || [];
};

export const getCriticLogo = (shortName) => {
  return shortName ? `${SVG_SERVER_URL}/logo-${shortName.toLowerCase()}.svg` : null;
};

export const getAllProductsBrandLabel = (t, region, brand, isSpirit = false) => {
  if (isSpirit === true) {
    return t('allSpiritsBrand', { brand });
  }
  return region === 'Champagne' ? t('allChampagnesBrand', { brand }) : t('allWinesBrand', { brand });
};

export const getItemList = (product) => product?.items || [];

// TODO: make the following test easier to read and understand
export const getStockLabel = (itemsInPromo, ribbonad, product) =>
  itemsInPromo?.length === 0 && (ribbonad) ? '' : getAttributeValue(product, 'in_stock', '');

export const getItemsInPromo = (items) =>
  items?.filter((item) => +getAttributeId(item, 'promotion_o_n', 0) === 1) ?? [];

export const getProducerCateg = (categ, product, storeId) =>
  categ?._id === ('producteur_' + getAttributeId(product, 'producteur', '') + '-' + storeId) ?? '';

export const getItemPrice = (item) =>
  item.listPrice <= item.offerPrice ? item.listPrice : item.offerPrice;

export const getPromotionStyle = (product, currentItem, productCode) => {
  const isPromo = getAttributeId(currentItem, 'promotion_o_n', 0);
  let style = {};

  if (product.op) {
    const showBackgroungForPrimeur = !isPrimeurCode(productCode) || product.op.isOnPrimeur;
    const showBackgroungForPromo = !isPromo || !product.op.isOnPromo || !product.op.imgBackgrounds;
    if (showBackgroungForPrimeur || showBackgroungForPromo) {
      const min = 0;
      const max = +product.op.imgBackgrounds.length;
      const random = Math.floor(Math.random() * (max - min)) + min;
      style = {
        specialStyle: {
          backgroundImage: `url(${product.op.imgBackgrounds[random]})`,
          backgroundRepeat: 'no-repeat'
        },
        specialStyleNotHover: true
      };
    }
  }
  return style;
};

export const getProductPageRedirectionInfos = ({ _id, seoKeyword, isSpirit }, { partnumber }, storeConf) => {
  const linkProps = {};
  const normalizedSeoKeyword = normalizeUrl(seoKeyword);

  if (isKrugOrWhispering(seoKeyword)) {
    linkProps.href = normalizedSeoKeyword;
  } else {
    linkProps.href = getHrefForProductLink(seoKeyword, _id, partnumber, isSpirit);
    linkProps.as = formatUrl(seoKeyword, isSpirit, storeConf);
  }

  return linkProps;
};
